import React, { useState, useEffect } from "react";
import config from "../../common/Config";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Iconify } from "../../Minimal/src/components/iconify/iconify";
import "../../common/commonStyles.css";

const SupplierSuggestionList = ({ supplierArray }) => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem('token');
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierFilteredOptions, setSupplierFilteredOptions] = useState([]);
  const [productValue, setProductValue] = useState(null);
  const [productInputValue, setProductInputValue] = useState("");
  const [newProductList, setNewProductList] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [errorDialog, setErrorDialog] = useState({ open: false, message: "" });

  const supplierHandleInputChange = (inputValue) => {
    setProductInputValue(inputValue);
    const newFilteredOptions = supplierOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    setSupplierFilteredOptions(newFilteredOptions);
  };

  const supplierHandleChange = (selectedOption) => {
    if (selectedOption) {
      setProductValue(selectedOption);
      fetchAddSupplierInfo(selectedOption.value);
    } else {
      setProductValue(null);
    }
  };

  const fetchAddSupplierInfo = async (selectedOption) => {
    const formData = new FormData();
    formData.append("id", selectedOption);

    const existingProduct = tableRows.find(
      (item) => item.id === selectedOption
    );

    if (existingProduct) {
      setErrorDialog({ open: true, message: "Supplier already exists in the list" });
      return;
    }

    const apiUrl = `${config.API_URL}/get_supplier_byId`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch new product");
      }

      const data = await response.json();
      const supplierData = data.data;

      setNewProductList(data.supplierData);

      const newRow = {
        id: supplierData.id,
        company: supplierData.company,
      };

      setTableRows((prevRows) => [...prevRows, newRow]);
    } catch (error) {
    }
  };

  const fetchSupplierList = async () => {
    const apiSalesRouteUrl = `${config.API_URL}/purchases/getSupplier`;
    try {
      const response = await fetch(apiSalesRouteUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      const formattedOptions = data.supplier.map((item) => ({
        label: item.company,
        value: item.id,
      }));
      setSupplierOptions(formattedOptions);
      setSupplierFilteredOptions(formattedOptions);
    } catch (error) {
    }
  };

  const handleCancelSupplier = (id) => {
    setTableRows((prevRows) => prevRows.filter((row) => row.id !== id));
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchSupplierList();
  }, []);

  useEffect(() => {
    if (supplierArray === undefined) return;
    const rows = (supplierArray || []).map((supplier) => ({
      id: supplier.supplier_id ?? supplier.id,
      company: supplier.company ?? supplier.company_name ?? "",
    }));
    setTableRows(rows);
  }, [supplierArray]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          size="small"
          isOptionEqualToValue={(option, value) => option.value === value.value}
          options={supplierFilteredOptions}
          value={productValue}
          onChange={(event, newValue) => supplierHandleChange(newValue)}
          inputValue={productInputValue}
          onInputChange={(event, newInputValue) => supplierHandleInputChange(newInputValue)}
          getOptionLabel={(option) => option.label || ''}
          renderInput={(params) => (
            <TextField className="input-border" {...params} placeholder="Select Supplier" />
          )}
        />
      </Box>

      <TableContainer sx={{ border: "1px solid rgba(145, 158, 171, 0.16)", borderRadius: 1, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 300 }} className="table-cell-border">
          <TableHead>
            <TableRow  className="table-cell-header">
              <TableCell sx={{ color: "#637387", fontWeight: 600, width: { xs: "70%", sm: "85%" } }}>Supplier</TableCell>
              <TableCell sx={{ color: "#637387", fontWeight: 600, width: { xs: "30%", sm: "15%" }, textAlign: "center" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} sx={{ textAlign: "center", py: 2}}>
                  No Supplier Added
                </TableCell>
              </TableRow>
            ) : (
              tableRows.map((row) => (
                <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "rgba(145, 158, 171, 0.08)" } }}>
                  <TableCell sx={{ wordBreak: "break-word" }}>
                    <input type="hidden" value={row.id} name="supplier_id[]" />
                    {row.company}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleCancelSupplier(row.id)}
                    >
                      <Iconify icon="mdi:trash-outline" width={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ open: false, message: "" })}
      >
        <DialogTitle sx={{ color: "error.main" }}>Failed!</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialog({ open: false, message: "" })} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierSuggestionList;
